/**
 * Toast notification utilities
 * Wraps react-hot-toast with consistent styling using TalkToast theme
 */

import toast from 'react-hot-toast'
import { colors } from '@talktoast/ui'

// Base toast style configuration
const BASE_TOAST_STYLE = {
  padding: '16px',
  borderRadius: '8px',
  fontSize: '14px',
  fontWeight: '500',
} as const

const TOAST_CONFIG = {
  position: 'top-center' as const,
  style: BASE_TOAST_STYLE,
}

export const showSuccess = (message: string) => {
  return toast.success(message, {
    ...TOAST_CONFIG,
    duration: 3000,
    style: {
      ...BASE_TOAST_STYLE,
      background: colors.success,
      color: colors.white,
    },
  })
}

export const showError = (message: string) => {
  return toast.error(message, {
    ...TOAST_CONFIG,
    duration: 4000,
    style: {
      ...BASE_TOAST_STYLE,
      background: colors.error,
      color: colors.white,
    },
  })
}

export const showInfo = (message: string) => {
  return toast(message, {
    ...TOAST_CONFIG,
    duration: 3000,
    icon: 'ℹ️',
    style: {
      ...BASE_TOAST_STYLE,
      background: colors.info,
      color: colors.white,
    },
  })
}

export const showWarning = (message: string) => {
  return toast(message, {
    ...TOAST_CONFIG,
    duration: 4000,
    icon: '⚠️',
    style: {
      ...BASE_TOAST_STYLE,
      background: colors.warning,
      color: colors.deepCharcoal,
    },
  })
}

/**
 * Show FTUE (First-Time User Experience) tooltip
 * Auto-dismisses after 5 seconds
 */
export const showFTUE = (message: string, duration = 5000) => {
  return toast(message, {
    position: 'top-center',
    duration,
    icon: '💡',
    style: {
      ...BASE_TOAST_STYLE,
      background: colors.info,
      color: colors.white,
    },
  })
}

/**
 * Show recipe replacement confirmation
 */
export const showRecipeReplaced = (oldRecipe: string, newRecipe: string) => {
  return toast.success(`🔄 Replaced "${oldRecipe}" with "${newRecipe}"`, {
    position: 'bottom-right',
    duration: 4000,
    style: {
      ...BASE_TOAST_STYLE,
      background: colors.success,
      color: colors.white,
    },
  })
}

/**
 * Show meal scheduled confirmation
 */
export const showMealScheduled = (recipe: string, date: string) => {
  return toast.success(`📅 Scheduled "${recipe}" for ${date}`, {
    position: 'bottom-right',
    duration: 3000,
    style: {
      ...BASE_TOAST_STYLE,
      background: colors.success,
      color: colors.white,
    },
  })
}

/**
 * Show favorite added confirmation
 */
export const showFavoriteAdded = (recipe: string) => {
  return toast.success(`⭐ Added "${recipe}" to favorites`, {
    position: 'bottom-right',
    duration: 2000,
    style: {
      ...BASE_TOAST_STYLE,
      background: colors.success,
      color: colors.white,
    },
  })
}

/**
 * Show shopping list ready notification
 */
export const showShoppingListReady = (itemCount: number) => {
  return toast.success(`🛒 Shopping list ready with ${itemCount} item${itemCount !== 1 ? 's' : ''}`, {
    position: 'bottom-right',
    duration: 3000,
    style: {
      ...BASE_TOAST_STYLE,
      background: colors.success,
      color: colors.white,
    },
  })
}

/**
 * Show feature gate notification (subscription required)
 */
export const showFeatureGate = (featureName: string, requiredTier: string) => {
  return toast(`🌟 ${featureName} requires ${requiredTier} subscription`, {
    position: 'top-center',
    duration: 4000,
    icon: '🔒',
    style: {
      ...BASE_TOAST_STYLE,
      background: colors.warning,
      color: colors.deepCharcoal,
    },
  })
}

// Export toast for custom usage
export { toast }
